// src/hooks/admin/edu/useIntegration.js

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get, post } from "@utils/axios_api";
import { downloadExcel } from "@utils/downloadExcel";

// API 호출 함수 정의 (Lecture와 동일한 구조)
const fetchIntegrationList = async (params) => {
  const response = await get("/admin/integ/integrationList", { params });
  return response;
};

const fetchCategoryList = async () => {
  const response = await get("/common/categoryList");
  return response;
};

const fetchEduTypeList = async (groupCode) => {
  const response = await post("/common/codeList", { groupCode });
  return response;
};

export function useIntegration() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedItems, setSelectedItems] = useState([]);


  const eduCategoryId = searchParams.get("eduCategoryId") || "";
  const eduType = searchParams.get("eduType") || "";
  const srcStartDate = searchParams.get("srcStartDate") || "";
  const srcEndDate = searchParams.get("srcEndDate") || "";
  const searchCnd = searchParams.get("searchCnd") || "";
  const searchWrd = searchParams.get("searchWrd") || "";
  const pageIndex = parseInt(searchParams.get("pageIndex") || "1", 10);
  const recordCountPerPage = 10;

  const queryParam = {
    eduCategoryId,
    eduType,
    srcStartDate,
    srcEndDate,
    searchCnd,
    searchWrd,
    pageIndex,
    recordCountPerPage,
  };

  const { data: integrationList, refetch } = useQuery({
    queryKey: ["selectIntegrationList", JSON.stringify(queryParam)],
    queryFn: () => fetchIntegrationList(queryParam),
    keepPreviousData: true,
  });

  const { data: categoryList } = useQuery({
    queryKey: ["selectAdminCategoryList"],
    queryFn: fetchCategoryList,
    staleTime: Infinity,
  });

  const { data: eduTypeList } = useQuery({
    queryKey: ["selectAdminEduTypeList", "EDU_TYPE"],
    queryFn: () => fetchEduTypeList("EDU_TYPE"),
    staleTime: Infinity,
  });

  const refreshList = () => {
    refetch();
  };

  const onSubmit = (values) => {
    const newParams = new URLSearchParams();
    newParams.set("eduCategoryId", values.eduCategoryId || "");
    newParams.set("eduType", values.eduType || "");
    newParams.set("srcStartDate", values.srcStartDate || "");
    newParams.set("srcEndDate", values.srcEndDate || "");
    newParams.set("searchCnd", values.searchCnd || "");
    newParams.set("searchWrd", values.searchWrd || "");
    newParams.set("pageIndex", "1");
    setSearchParams(newParams);
  };

  const handlePageChange = (targetPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("pageIndex", targetPage);
    setSearchParams(newParams);
  };

  const handleSelectAll = (checked) => {          
    if (!integrationList?.resultList) return;
    if (checked) {
      const allKeys = integrationList.resultList.map(
        (it) => `${it.lectureId}-${it.memberId}`
      );
      setSelectedItems(allKeys);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (key) => {             
    setSelectedItems((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const resetSearch = () => {
    setSearchParams(new URLSearchParams());
  };

  // 체크 여부
  const isAllChecked =
    integrationList?.resultList?.length > 0 &&
    selectedItems.length === integrationList?.resultList?.length;
    const downloadZip = async () => {
      if (!selectedItems.length) {
        alert("다운로드할 항목을 선택하세요.");
        return;
      }
    
      // ① payload 구성
      const payload = selectedItems.map((key) => {
        const [lectureId, memberId] = key.split("-");
        return { lectureId, memberId };
      });
    
      try {
        // 공통 래퍼 post 사용 (headers·data 그대로 반환)
        const { data, headers } = await post(
          "/admin/integ/issueImpl/bulk",
          payload,
          { responseType: "blob", timeout: 10000 }   // <-- blob 모드
        );

        const ctype = headers["content-type"] || "";
        if (ctype.includes("application/json")) {
          const text   = await data.text();          // Blob → text
          const { message } = JSON.parse(text);
          alert(message);                            // “교육 이수자만 …”
          return;
        }
    
        const disposition = headers["content-disposition"] || "";
        const match = disposition.match(/filename\*=UTF-8''(.+)/);
        const fileName = match ? decodeURIComponent(match[1]) : "확인서.zip";

        // 정상적인 Blob 파일일 경우만 다운로드 처리
        const blobUrl = URL.createObjectURL(new Blob([data]));
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(blobUrl);

      } catch (err) {
        alert(err?.message || "다운로드 중 오류가 발생했습니다.");
      }
    };
      
  const handleExcelDownload = (e) => {
    e.preventDefault();
    downloadExcel({
      url: "/admin/integ/integrationListExcelDown",
      params: queryParam,
      filePrefix: "통합수강생",
    });
  };


  return {
    integrationList,
    categoryList,
    eduTypeList,
    selectedItems,
    isAllChecked,
    onSubmit,
    handlePageChange,
    handleSelectAll,
    handleSelectItem,
    pageIndex,
    recordCountPerPage,
    refetch,
    refreshList,
    downloadZip,
    handleExcelDownload,
    resetSearch,
  };
}
